import { Quote as QuoteIcon, Volume2, Video } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import CopyButton from '@/components/ui/CopyButton';
import useTranscriptPanelStore from '@/store/TranscriptPanelStore';
import type { VoiceQuote } from '@/types';

function formatProfileSummary(profile: Record<string, string>): string {
  const parts: string[] = [];
  // Prioritize key demographic fields
  if (profile.gender) parts.push(profile.gender);
  if (profile.age) parts.push(profile.age);
  if (profile.social_class) parts.push(profile.social_class);
  // If no standard demographics, take first 2 values
  if (parts.length === 0) {
    const values = Object.values(profile).slice(0, 2);
    parts.push(...values);
  }
  return parts.join(', ');
}

interface VoiceQuotesProps {
  quotes: VoiceQuote[];
  isLoading: boolean;
  showQuestionLabel?: boolean;
  questionMap?: Record<string, string>;
}

function VoiceQuotes({ quotes, isLoading, showQuestionLabel, questionMap }: VoiceQuotesProps) {
  const { open: openTranscriptPanel } = useTranscriptPanelStore();

  const handleRespondentClick = (conversationId: string, messageId?: string) => {
    openTranscriptPanel(conversationId, messageId);
  };

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-neutral-200 p-5 flex flex-col gap-3 animate-pulse"
          >
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-4 w-full rounded-sm" />
            <Skeleton className="h-4 w-[80%] rounded-sm" />
            <Skeleton className="h-4 w-[60%] rounded-sm" />
          </div>
        ))}
      </div>
    );
  }

  if (!quotes || quotes.length === 0) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {quotes.map((quote, index) => (
        <div
          key={`${quote.conversation_id}-${index}`}
          className="bg-white rounded-lg border border-neutral-200 p-5 flex flex-col gap-3"
        >
          <div className="flex items-start justify-between">
            <QuoteIcon className="h-6 w-6 text-neutral-300 shrink-0" />
            <CopyButton
              text={`"${quote.quote}"${quote.respondent_label ? ` — ${quote.respondent_label}` : quote.respondent_index ? ` — Respondent #${quote.respondent_index}` : ''}`}
            />
          </div>
          <p className="text-sm font-medium text-neutral-800 leading-relaxed">
            &ldquo;{quote.quote}&rdquo;
          </p>
          {quote.original_quote && (
            <div className="flex flex-col gap-1">
              <p className="text-sm italic text-neutral-500 leading-relaxed">
                &ldquo;{quote.original_quote}&rdquo;
              </p>
              {quote.original_language && (
                <span className="text-xs text-neutral-400 bg-neutral-100 px-2 py-0.5 rounded w-fit">
                  {quote.original_language}
                </span>
              )}
            </div>
          )}
          {showQuestionLabel && quote.question_id && questionMap?.[quote.question_id] && (
            <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded w-fit line-clamp-1">
              {questionMap[quote.question_id]}
            </span>
          )}
          {quote.media_url && quote.media_type === 'audio' && (
            <audio src={quote.media_url} controls preload="none" className="w-full h-8" />
          )}
          {quote.media_url && quote.media_type === 'video' && (
            <video
              src={quote.media_url}
              controls
              preload="metadata"
              className="w-full rounded-md"
            />
          )}
          {quote.media_type && !quote.media_url && (
            <span className="inline-flex items-center gap-1 text-xs text-neutral-400">
              {quote.media_type === 'audio' ? (
                <Volume2 className="h-3 w-3" />
              ) : (
                <Video className="h-3 w-3" />
              )}
              {quote.media_type === 'audio' ? 'Audio response' : 'Video response'}
            </span>
          )}
          <button
            type="button"
            onClick={() => handleRespondentClick(quote.conversation_id, quote.message_id)}
            aria-label={`View transcript for respondent ${quote.respondent_index}`}
            className="text-xs font-medium text-primary hover:underline w-fit mt-auto"
          >
            Respondent #{quote.respondent_index}
            {(quote.respondent_label || quote.respondent_profile) && (
              <span className="text-neutral-400 font-normal">
                {' — '}
                {quote.respondent_label || formatProfileSummary(quote.respondent_profile!)}
              </span>
            )}
          </button>
        </div>
      ))}
    </div>
  );
}

export default VoiceQuotes;
